import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldRenderProps } from 'react-final-form';

import { LocationGroupType, OptionParameterFragment } from '@websky/graphql';
import { FieldProps } from '../Field';
import { PassengerField } from '../../../types';

interface UseNationalityArgs {
	options: OptionParameterFragment[];
	onChange: FieldProps['onChange'];
	field: PassengerField;
	fieldState: FieldRenderProps<string, HTMLInputElement>;
}

export const useNationality = ({ options, onChange, field, fieldState }: UseNationalityArgs) => {
	const { t } = useTranslation('Passenger');

	const [inputValue, setInputValue] = React.useState<string>('');
	const [selectedValue, setSelectedValue] = React.useState<string>('');
	const [isFiltering, setIsFiltering] = React.useState(false);

	const { optionsByValue, optionsByLabel } = useMemo(() => {
		const optionsByValue = new Map<string, OptionParameterFragment>();
		const optionsByLabel = new Map<string, OptionParameterFragment>();

		options.forEach(option => {
			const { value, label } = option;

			optionsByValue.set(value, option);
			optionsByLabel.set(label.toLowerCase(), option);
		});

		return { optionsByValue, optionsByLabel };
	}, [options]);

	const filteredOptions = useMemo(() => {
		if (isFiltering && inputValue) {
			return options.filter(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));
		} else {
			return options;
		}
	}, [options, isFiltering, inputValue]);

	const optionsGroup = useMemo(() => {
		const popularOptions: OptionParameterFragment[] = [];
		const otherOptions: OptionParameterFragment[] = [];

		filteredOptions.forEach(option => {
			if (option.priority > 0) {
				popularOptions.push(option);
			} else {
				otherOptions.push(option);
			}
		});

		popularOptions.sort((a, b) => b.priority - a.priority);

		const groups: { label: string; options: OptionParameterFragment[]; type: LocationGroupType }[] = [];

		if (popularOptions.length > 0) {
			groups.push({
				label: t('Popular countries'),
				options: popularOptions,
				type: LocationGroupType.Popular
			});
		}

		if (otherOptions.length > 0) {
			groups.push({
				label: t('Other countries'),
				options: otherOptions,
				type: LocationGroupType.Country
			});
		}

		return groups;
	}, [filteredOptions]);

	const findOptionByValue = useCallback((value: string) => optionsByValue.get(value), [optionsByValue]);

	const findOptionByLabel = useCallback((label: string) => optionsByLabel.get(label.toLowerCase()), [optionsByLabel]);

	const isExactMatch = useCallback((value: string) => optionsByLabel.has(value.toLowerCase()), [optionsByLabel]);

	useEffect(() => {
		const currentFieldValue = fieldState.input.value;

		// Если значение поля очищено извне
		if (!currentFieldValue) {
			setInputValue('');
			setSelectedValue('');

			return;
		}

		// Если значение поля изменилось и оно отличается от текущего выбранного значения
		if (currentFieldValue !== selectedValue) {
			const currentOption = findOptionByValue(currentFieldValue);
			if (currentOption) {
				setInputValue(currentOption.label);
				setSelectedValue(currentOption.value);
			}
		}
	}, [fieldState.input.value, selectedValue, findOptionByValue]);

	const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value);
		setIsFiltering(true);
	}, []);

	const onSelect = useCallback(
		(optionValue: string) => {
			const selectedOption = findOptionByValue(optionValue);
			if (selectedOption) {
				setInputValue(selectedOption.label);
				setSelectedValue(selectedOption.value);
				setIsFiltering(false);

				onChange(field.name, selectedOption.value);
			}
		},
		[findOptionByValue, onChange, field.name]
	);

	const onFocus: React.FocusEventHandler = useCallback(() => {
		setInputValue('');
		setIsFiltering(true);
	}, []);

	const onBlur = useCallback(() => {
		setIsFiltering(false);

		// Если есть только одна опция в отфильтрованном списке, выбираем её
		if (filteredOptions.length === 1) {
			const option = filteredOptions[0];
			setInputValue(option.label);
			setSelectedValue(option.value);
			onChange(field.name, option.value);

			return;
		}

		// Если введенное значение точно совпадает с существующей опцией
		if (inputValue && isExactMatch(inputValue)) {
			const matchedOption = findOptionByLabel(inputValue);
			if (matchedOption) {
				setSelectedValue(matchedOption.value);
				onChange(field.name, matchedOption.value);

				return;
			}
		}

		// Если есть уже выбранное значение, восстанавливаем его
		if (selectedValue) {
			const currentOption = findOptionByValue(selectedValue);
			if (currentOption) {
				setInputValue(currentOption.label);

				return;
			}
		}

		// Если ничего не подходит, очищаем поля
		setInputValue('');
		setSelectedValue('');
		onChange(field.name, null);
	}, [
		filteredOptions,
		inputValue,
		selectedValue,
		isExactMatch,
		findOptionByLabel,
		findOptionByValue,
		onChange,
		field.name
	]);

	return {
		value: inputValue,
		options: optionsGroup,
		onInputChange,
		onSelect,
		onFocus,
		onBlur
	};
};
